import React, { useState, useEffect, useCallback } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "react-quill/dist/quill.bubble.css";
import { useSelector } from "react-redux";
import { formatDriveUrl, normalizeDriveUrlForSave } from "../../../Utils/driveUtils";
import {
  Tabs,
  Form,
  Input,
  Button,
  Table,
  Select,
  Switch,
  Space,
  Tag,
  Row,
  Col,
  Card,
  message,
  Popconfirm,
  Tooltip,
  Badge,
  Modal,
  Avatar
} from "antd";
import {
  VideoCameraOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  BarChartOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  EyeOutlined,
  UserOutlined,
  InteractionOutlined,
  FileDoneOutlined,
  BookOutlined
} from "@ant-design/icons";
import {
  getAllCoursesByRole,
  addCourseContent,
  editCourseContent,
  deleteCourseContent,
  addAssestment,
  editAssestment,
  deleteAssestment,
  getCouseAssestmentsByRole,
  getAllUsersOfACourse,
  addSession,
  getSessions,
  attendToSession,
  getAttendanceBySession,
  getSubmissionsByAssestment,
  reviewAssestmentSubmission
} from "../../../Constants/Api/functions/function";
import { getGradeDetails } from "../../../Utils/gradeUtils";

import "./style.scss";

const { TabPane } = Tabs;
const { TextArea } = Input;
const { Option } = Select;

const quillModules = {
  toolbar: [
    [{ font: [] }],
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    [{ align: [] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ script: "sub" }, { script: "super" }],
    ["blockquote", "code-block"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "image", "video"],
    ["clean"],
  ],
};

const InstructorPanel = () => {
  const userProfile = useSelector((state) => state.userProfile.userProfile);
  const [courses, setCourses] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [, setSelectedCourse] = useState(null);
  const [loading, setLoading] = useState(false);

  // Tab data states
  const [contents, setContents] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [students, setStudents] = useState([]);
  const [attendance, setAttendance] = useState({}); // session ID -> list of present student user IDs

  // Modal / Form Edit States
  const [isContentModalOpen, setIsContentModalOpen] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [contentForm] = Form.useForm();

  const [isAssessmentModalOpen, setIsAssessmentModalOpen] = useState(false);
  const [editingAssessment, setEditingAssessment] = useState(null);
  const [assessmentForm] = Form.useForm();

  const [isSessionModalOpen, setIsSessionModalOpen] = useState(false);
  const [sessionForm] = Form.useForm();

  const [isGradingModalOpen, setIsGradingModalOpen] = useState(false);
  const [gradingSubmissions, setGradingSubmissions] = useState([]);
  const [selectedGradingAssessment, setSelectedGradingAssessment] = useState(null);
  const [gradingForm] = Form.useForm();

  // Load instructor courses
  const fetchInstructorCourses = useCallback(async () => {
    if (!userProfile?.id) return;
    setLoading(true);
    try {
      // instructors can fetch all their courses
      const res = await getAllCoursesByRole("instructor", userProfile.id);
      if (res && Array.isArray(res)) {
        setCourses(res);
        if (res.length > 0) {
          setSelectedCourseId(res[0].id);
          setSelectedCourse(res[0]);
        }
      }
    } catch (err) {
      message.error("Failed to load courses.");
    } finally {
      setLoading(false);
    }
  }, [userProfile?.id]);

  useEffect(() => {
    fetchInstructorCourses();
  }, [fetchInstructorCourses]);

  // Load course details, contents, assessments, and attendance sessions
  const fetchCourseData = useCallback(async () => {
    if (!selectedCourseId) return;
    setLoading(true);
    try {
      // 1. Fetch course details to get latest materials & lessons
      const response = await getAllCoursesByRole("instructor", userProfile?.id);
      const active = response?.find((c) => c.id === selectedCourseId);
      if (active) {
        setSelectedCourse(active);
        setContents(active.courseContents || []);
      }

      // 2. Fetch assessments
      const assessData = await getCouseAssestmentsByRole(
        selectedCourseId,
        "instructor",
        userProfile?.id
      );
      if (assessData && Array.isArray(assessData)) {
        setAssessments(assessData);
      }

      // 3. Fetch meeting sessions
      const sessionData = await getSessions("instructor", userProfile?.id);
      if (sessionData && Array.isArray(sessionData)) {
        setSessions(sessionData.filter((s) => s.courseId === selectedCourseId));
      }

      // 4. Fetch students enrolled in this course
      const studentData = await getAllUsersOfACourse(selectedCourseId);
      if (studentData && Array.isArray(studentData)) {
        setStudents(studentData);
      }
    } catch (err) {
      console.error(err);
      message.error("Failed to fetch course dashboard details.");
    } finally {
      setLoading(false);
    }
  }, [selectedCourseId, userProfile?.id]);

  useEffect(() => {
    fetchCourseData();
  }, [fetchCourseData]);

  const handleCourseChange = (value) => {
    setSelectedCourseId(value);
    const selected = courses.find((c) => c.id === value);
    setSelectedCourse(selected);
  };

  // --- UPLOAD VIDEOS & LESSON MATERIALS ---
  const handleContentSubmit = async (values) => {
    try {
      const payload = {
        ...values,
        driveUrl: normalizeDriveUrlForSave(values.driveUrl),
        courseId: selectedCourseId,
        createdBy: userProfile?.id,
        isFree: values.isFree ? 1 : 0,
        isPublished: values.isPublished ? 1 : 0,
        installment: parseInt(values.installment || 0, 10)
      };

      if (editingContent) {
        payload.id = editingContent.id;
        const res = await editCourseContent(payload);
        if (res && res.success !== false) {
          message.success("Lesson content updated successfully!");
        } else {
          message.error(res?.message || "Failed to update content.");
        }
      } else {
        const res = await addCourseContent(payload);
        if (res && res.success !== false) {
          message.success("Lesson content uploaded successfully!");
        } else {
          message.error(res?.message || "Failed to add content.");
        }
      }
      setIsContentModalOpen(false);
      setEditingContent(null);
      contentForm.resetFields();
      fetchCourseData();
    } catch (err) {
      message.error("Error occurred while saving content.");
    }
  };

  const handleContentDelete = async (contentId) => {
    try {
      const res = await deleteCourseContent(contentId);
      if (res && res.success !== false) {
        message.success("Lesson content deleted.");
        fetchCourseData();
      } else {
        message.error("Failed to delete content.");
      }
    } catch (err) {
      message.error("Error deleting content.");
    }
  };

  // --- CREATE ASSIGNMENTS & QUIZZES ---
  const handleAssessmentSubmit = async (values) => {
    try {
      const payload = {
        ...values,
        courseId: selectedCourseId,
        createdBy: userProfile?.id,
        isPublished: values.isPublished ? 1 : 0
      };

      if (editingAssessment) {
        payload.id = editingAssessment.id;
        const res = await editAssestment(payload);
        if (res && res.success !== false) {
          message.success("Assessment updated successfully!");
        } else {
          message.error("Failed to update assessment.");
        }
      } else {
        const res = await addAssestment(payload);
        if (res && res.success !== false) {
          message.success("Assessment created successfully!");
        } else {
          message.error("Failed to create assessment.");
        }
      }
      setIsAssessmentModalOpen(false);
      setEditingAssessment(null);
      assessmentForm.resetFields();
      fetchCourseData();
    } catch (err) {
      message.error("Error occurred while saving assessment.");
    }
  };

  const handleAssessmentDelete = async (id) => {
    try {
      const res = await deleteAssestment(id);
      if (res && res.success !== false) {
        message.success("Assessment deleted successfully.");
        fetchCourseData();
      } else {
        message.error("Failed to delete assessment.");
      }
    } catch (err) {
      message.error("Error deleting assessment.");
    }
  };

  // --- STUDENT ATTENDANCE TRACKER ---
  const handleSessionSubmit = async (values) => {
    try {
      const payload = {
        ...values,
        courseId: selectedCourseId,
        createdBy: userProfile?.id,
        isFree: values.isFree ? 1 : 0,
        isRepeat: 0,
        isPublished: 1
      };

      const res = await addSession(payload);
      if (res && res.success !== false) {
        message.success("Class session scheduled successfully!");
        setIsSessionModalOpen(false);
        sessionForm.resetFields();
        fetchCourseData();
      } else {
        message.error("Failed to create session.");
      }
    } catch (err) {
      message.error("Error scheduled session.");
    }
  };

  const loadSessionAttendance = async (sessionId) => {
    try {
      const res = await getAttendanceBySession(sessionId);
      if (res && Array.isArray(res)) {
        const presentIds = res.map((r) => r.userId);
        setAttendance((prev) => ({
          ...prev,
          [sessionId]: presentIds
        }));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const toggleAttendance = async (sessionId, studentId, isCurrentlyPresent) => {
    try {
      // attendToSession serves as record toggler or presence recorder
      await attendToSession(sessionId, studentId, "false");
      
      // Update local state instantly
      const currentPresent = attendance[sessionId] || [];
      let newPresent;
      if (isCurrentlyPresent) {
        newPresent = currentPresent.filter((id) => id !== studentId);
      } else {
        newPresent = [...currentPresent, studentId];
      }
      setAttendance((prev) => ({
        ...prev,
        [sessionId]: newPresent
      }));
      message.success("Attendance successfully synchronized.");
    } catch (err) {
      message.error("Failed to update student attendance.");
    }
  };

  // --- SUBMISSION GRADING HUB ---
  const openGradingSubmissions = async (assessment) => {
    setSelectedGradingAssessment(assessment);
    setLoading(true);
    try {
      const res = await getSubmissionsByAssestment(assessment.id);
      if (res && Array.isArray(res)) {
        setGradingSubmissions(res);
        setIsGradingModalOpen(true);
      } else {
        message.info("No student submissions found for this assessment.");
      }
    } catch (err) {
      message.error("Failed to load submissions.");
    } finally {
      setLoading(false);
    }
  };

  const handleGradeSubmit = async (values) => {
    try {
      const payload = {
        id: values.submissionId,
        submissionId: values.submissionId,
        marks: values.marks,
        comments: values.comments,
        reviewdBy: userProfile?.id
      };
      const res = await reviewAssestmentSubmission(payload);
      if (res && res.success !== false) {
        message.success("Student submission marked and reviewed successfully!");
        setIsGradingModalOpen(false);
        gradingForm.resetFields();
        fetchCourseData();
      } else {
        message.error("Failed to save grading feedback.");
      }
    } catch (err) {
      message.error("Error occurred while reviewing grade.");
    }
  };

  return (
    <div className="instructor-panel">
      {/* 1. Header & Course Selection Deck */}
      <div className="glass-header">
        <Row align="middle" justify="space-between" gutter={[16, 16]}>
          <Col xs={24} md={14}>
            <div className="title-area">
              <Avatar
                size={56}
                src={userProfile?.profileImage ? `${userProfile.profileImage}` : null}
                icon={<UserOutlined />}
                className="instructor-avatar"
              />
              <div>
                <h1>Instructor Control Center</h1>
                <p>
                  Branch Active:{" "}
                  <Tag color="cyan">
                    {userProfile?.centerId === 2 ? "IBAS Center" : "Digital Academy"}
                  </Tag>{" "}
                  | Lecturer: <strong>{userProfile?.name}</strong>
                </p>
              </div>
            </div>
          </Col>
          <Col xs={24} md={10} style={{ textAlign: "right" }}>
            <span className="course-select-label">Selected Course: </span>
            <Select
              className="course-select-box"
              placeholder="Select a Course"
              value={selectedCourseId}
              onChange={handleCourseChange}
              dropdownClassName="glass-dropdown"
              showSearch
              optionFilterProp="children"
            >
              {[...courses]
                .sort((a, b) => {
                  const statusA = (a?.status || 'ongoing').toLowerCase();
                  const statusB = (b?.status || 'ongoing').toLowerCase();
                  if (statusA === 'ongoing' && statusB !== 'ongoing') return -1;
                  if (statusA !== 'ongoing' && statusB === 'ongoing') return 1;
                  return 0;
                })
                .map((course) => (
                  <Option key={course.id} value={course.id}>
                    {course.name} ({course.status === 'completed' ? 'Completed' : 'Ongoing'})
                  </Option>
                ))}
            </Select>
          </Col>
        </Row>
      </div>

      {selectedCourseId ? (
        <>
          {/* 2. Quick Dashboard Indicators */}
          <Row gutter={[16, 16]} className="stats-row">
            <Col xs={12} sm={6}>
              <Card className="metric-card cyan-glow">
                <Badge count="Students" className="card-badge" />
                <div className="metric-content">
                  <UserOutlined className="metric-icon" />
                  <div>
                    <h3>{students.length}</h3>
                    <p>Total Enrolled</p>
                  </div>
                </div>
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card className="metric-card magenta-glow">
                <Badge count="Lessons" className="card-badge" />
                <div className="metric-content">
                  <VideoCameraOutlined className="metric-icon" />
                  <div>
                    <h3>{contents.length}</h3>
                    <p>Videos/Material</p>
                  </div>
                </div>
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card className="metric-card yellow-glow">
                <Badge count="Tasks" className="card-badge" />
                <div className="metric-content">
                  <FileTextOutlined className="metric-icon" />
                  <div>
                    <h3>{assessments.length}</h3>
                    <p>Assignments/Quiz</p>
                  </div>
                </div>
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card className="metric-card green-glow">
                <Badge count="Live Class" className="card-badge" />
                <div className="metric-content">
                  <BarChartOutlined className="metric-icon" />
                  <div>
                    <h3>{sessions.length}</h3>
                    <p>Sessions Slotted</p>
                  </div>
                </div>
              </Card>
            </Col>
          </Row>

          {/* 3. Main Operational Tab Deck */}
          <div className="tab-deck">
            <Tabs defaultActiveKey="videos" type="card">
              {/* Tab A: UPLOAD VIDEOS & LESSON MATERIALS */}
              <TabPane
                tab={
                  <span>
                    <VideoCameraOutlined /> Videos & Materials
                  </span>
                }
                key="videos"
              >
                <div className="tab-actions">
                  <h2>Lesson Content Management</h2>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      setEditingContent(null);
                      contentForm.resetFields();
                      setIsContentModalOpen(true);
                    }}
                  >
                    Add Lesson content
                  </Button>
                </div>
                <Table
                  dataSource={contents}
                  rowKey="id"
                  loading={loading}
                  pagination={{ pageSize: 5 }}
                  className="glass-table"
                >
                  <Table.Column title="Lesson Title" dataIndex="title" key="title" />
                  <Table.Column
                    title="Installment Limit"
                    dataIndex="installment"
                    key="installment"
                    render={(inst) => (
                      <Tag color={inst > 0 ? "orange" : "green"}>
                        {inst > 0 ? `Installment ${inst}` : "Free for All"}
                      </Tag>
                    )}
                  />
                  <Table.Column
                    title="Video Link"
                    dataIndex="videoUrl"
                    key="videoUrl"
                    render={(link) =>
                      link ? (
                        <a href={link} target="_blank" rel="noreferrer">
                          Watch Video
                        </a>
                      ) : (
                        <span className="text-muted">N/A</span>
                      )
                    }
                  />
                  <Table.Column
                    title="Folder Link"
                    dataIndex="driveUrl"
                    key="driveUrl"
                    render={(link) => {
                      const formatted = formatDriveUrl(link);
                      return formatted ? (
                        <a href={formatted.directUrl} target="_blank" rel="noreferrer">
                          Google Drive
                        </a>
                      ) : (
                        <span className="text-muted">N/A</span>
                      );
                    }}
                  />
                  <Table.Column
                    title="Live Zoom"
                    dataIndex="zoomUrl"
                    key="zoomUrl"
                    render={(link) =>
                      link ? (
                        <a href={link} target="_blank" rel="noreferrer">
                          Zoom Link
                        </a>
                      ) : (
                        <span className="text-muted">N/A</span>
                      )
                    }
                  />
                  <Table.Column
                    title="Actions"
                    key="actions"
                    render={(record) => (
                      <Space>
                        <Button
                          icon={<EditOutlined />}
                          onClick={() => {
                            setEditingContent(record);
                            contentForm.setFieldsValue({
                              ...record,
                              isFree: record.isFree === 1 || record.isFree === true,
                              isPublished: record.isPublished === 1 || record.isPublished === true
                            });
                            setIsContentModalOpen(true);
                          }}
                        />
                        <Popconfirm
                          title="Are you sure you want to delete this lesson?"
                          onConfirm={() => handleContentDelete(record.id)}
                          okText="Delete"
                          cancelText="Cancel"
                        >
                          <Button danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                      </Space>
                    )}
                  />
                </Table>
              </TabPane>

              {/* Tab B: CREATE ASSIGNMENTS & QUIZZES */}
              <TabPane
                tab={
                  <span>
                    <FileTextOutlined /> Assignments & Quizzes
                  </span>
                }
                key="tasks"
              >
                <div className="tab-actions">
                  <h2>Assignments & Quizzes</h2>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      setEditingAssessment(null);
                      assessmentForm.resetFields();
                      setIsAssessmentModalOpen(true);
                    }}
                  >
                    Add Assignment / Quiz
                  </Button>
                </div>
                <Table
                  dataSource={assessments}
                  rowKey="id"
                  loading={loading}
                  pagination={{ pageSize: 5 }}
                  className="glass-table"
                >
                  <Table.Column title="Task Name" dataIndex="name" key="name" />
                  <Table.Column title="Description" dataIndex="description" key="description" ellipsis render={(text) => text ? text.replace(/<[^>]*>/g, '') : ''} />
                  <Table.Column
                    title="Resource URL"
                    dataIndex="assessmentUrl"
                    key="assessmentUrl"
                    render={(link) =>
                      link ? (
                        <a href={link} target="_blank" rel="noreferrer">
                          Open Resource
                        </a>
                      ) : (
                        "-"
                      )
                    }
                  />
                  <Table.Column title="Due Date" dataIndex="endDate" key="endDate" />
                  <Table.Column
                    title="Status"
                    dataIndex="isPublished"
                    key="isPublished"
                    render={(pub) => (
                      <Tag color={pub === 1 ? "green" : "red"}>
                        {pub === 1 ? "Published" : "Draft"}
                      </Tag>
                    )}
                  />
                  <Table.Column
                    title="Actions"
                    key="actions"
                    render={(record) => (
                      <Space>
                        <Tooltip title="View submissions & grade marks">
                          <Button
                            type="primary"
                            ghost
                            icon={<FileDoneOutlined />}
                            onClick={() => openGradingSubmissions(record)}
                          />
                        </Tooltip>
                        <Button
                          icon={<EditOutlined />}
                          onClick={() => {
                            setEditingAssessment(record);
                            assessmentForm.setFieldsValue({
                              ...record,
                              isPublished: record.isPublished === 1 || record.isPublished === true
                            });
                            setIsAssessmentModalOpen(true);
                          }}
                        />
                        <Popconfirm
                          title="Are you sure you want to delete this task?"
                          onConfirm={() => handleAssessmentDelete(record.id)}
                          okText="Delete"
                          cancelText="Cancel"
                        >
                          <Button danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                      </Space>
                    )}
                  />
                </Table>
              </TabPane>

              {/* Tab C: STUDENT ATTENDANCE */}
              <TabPane
                tab={
                  <span>
                    <CheckCircleOutlined /> Student Attendance
                  </span>
                }
                key="attendance"
              >
                <div className="tab-actions">
                  <h2>Class Sessions & Meeting Attendance</h2>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setIsSessionModalOpen(true)}
                  >
                    Schedule Live Class / Session
                  </Button>
                </div>
                <Table
                  dataSource={sessions}
                  rowKey="id"
                  loading={loading}
                  pagination={{ pageSize: 5 }}
                  className="glass-table"
                  expandable={{
                    expandedRowRender: (session) => {
                      // Fetch attendance when session row is expanded
                      if (!attendance[session.id]) {
                        loadSessionAttendance(session.id);
                      }
                      const presentList = attendance[session.id] || [];

                      return (
                        <div className="expanded-attendance">
                          <h4>
                            Mark Attendance Roster for Date: {session.meetingDate} at{" "}
                            {session.meetingTime}
                          </h4>
                          {students.length === 0 ? (
                            <p className="no-students">
                              No students enrolled in this course yet.
                            </p>
                          ) : (
                            <Row gutter={[12, 12]} className="student-attendance-grid">
                              {students.map((student) => {
                                const isPresent = presentList.includes(student.id);
                                return (
                                  <Col xs={24} sm={12} md={8} key={student.id}>
                                    <Card className={`student-att-card ${isPresent ? "present" : ""}`}>
                                      <Row align="middle" justify="space-between">
                                        <Space>
                                          <Avatar src={student.profileImage} icon={<UserOutlined />} />
                                          <div>
                                            <strong>{student.name}</strong>
                                            <p className="subtext">{student.indexNumber}</p>
                                          </div>
                                        </Space>
                                        <Switch
                                          checkedChildren="Present"
                                          unCheckedChildren="Absent"
                                          checked={isPresent}
                                          onChange={() =>
                                            toggleAttendance(session.id, student.id, isPresent)
                                          }
                                        />
                                      </Row>
                                    </Card>
                                  </Col>
                                );
                              })}
                            </Row>
                          )}
                        </div>
                      );
                    }
                  }}
                >
                  <Table.Column title="Class / Session" dataIndex="name" key="name" />
                  <Table.Column title="Date" dataIndex="meetingDate" key="meetingDate" />
                  <Table.Column title="Time" dataIndex="meetingTime" key="meetingTime" />
                  <Table.Column
                    title="Zoom Link"
                    dataIndex="meetingUrl"
                    key="meetingUrl"
                    render={(url) =>
                      url ? (
                        <a href={url} target="_blank" rel="noreferrer">
                          Join Meeting
                        </a>
                      ) : (
                        "-"
                      )
                    }
                  />
                  <Table.Column
                    title="Roster Action"
                    key="expander"
                    render={() => (
                      <span className="click-action-indicator">
                        <InteractionOutlined /> Expand row to mark presence
                      </span>
                    )}
                  />
                </Table>
              </TabPane>

              {/* Tab D: REPORTS & ANALYTICS */}
              <TabPane
                tab={
                  <span>
                    <BarChartOutlined /> Reports & Analytics
                  </span>
                }
                key="analytics"
              >
                <div className="tab-actions">
                  <h2>Student Enrollment & Payment Audit</h2>
                  <Button icon={<ReloadOutlined />} onClick={fetchCourseData}>
                    Refresh Reports
                  </Button>
                </div>
                <Table
                  dataSource={students}
                  rowKey="id"
                  loading={loading}
                  pagination={{ pageSize: 10 }}
                  className="glass-table"
                >
                  <Table.Column
                    title="Student Index"
                    dataIndex="indexNumber"
                    key="indexNumber"
                    render={(index) => <Tag color="blue">#{index}</Tag>}
                  />
                  <Table.Column
                    title="Student Details"
                    key="details"
                    render={(record) => (
                      <Space>
                        <Avatar src={record.profileImage} icon={<UserOutlined />} />
                        <div>
                          <strong>{record.name}</strong>
                          <p style={{ fontSize: "11px", color: "#888", margin: 0 }}>
                            {record.email}
                          </p>
                        </div>
                      </Space>
                    )}
                  />

                  <Table.Column
                    title="Enrolled On"
                    dataIndex="enrolledAt"
                    key="enrolledAt"
                    render={(date) => (date ? date.substring(0, 10) : "-")}
                  />
                  <Table.Column
                    title="Access Status"
                    key="access"
                    render={(record) => (
                      <Tag color={record.isPaid === 1 ? "green" : "red"}>
                        {record.isPaid === 1 ? "Paid / Active" : "Unpaid / Installment Pending"}
                      </Tag>
                    )}
                  />
                </Table>
              </TabPane>
            </Tabs>
          </div>
        </>
      ) : (
        <Card className="no-course-card">
          <BookOutlined className="empty-icon" />
          <h2>No Active Courses Found</h2>
          <p>
            You are not currently registered as the creator or instructor of any courses in this
            center. Create a course first in the Courses tab to access the dashboard tools!
          </p>
        </Card>
      )}

      {/* --- CONTENT UPLOAD MODAL --- */}
      <Modal
        title={editingContent ? "Edit Course Lesson" : "Upload Video & Course Materials"}
        visible={isContentModalOpen}
        onCancel={() => {
          setIsContentModalOpen(false);
          setEditingContent(null);
          contentForm.resetFields();
        }}
        footer={null}
        className="glass-modal"
      >
        <Form form={contentForm} layout="vertical" onFinish={handleContentSubmit}>
          <Form.Item
            label="Lesson Title"
            name="title"
            rules={[{ required: true, message: "Please input lesson title!" }]}
          >
            <Input placeholder="Enter lesson title..." />
          </Form.Item>
          <Form.Item label="Short Description" name="description">
            <ReactQuill theme="snow" modules={quillModules} placeholder="Enter a brief summary..." />
          </Form.Item>
          <Form.Item label="Video Link (YouTube / Vimeo / Drive URL)" name="videoUrl">
            <Input placeholder="e.g. https://youtube.com/watch?v=..." />
          </Form.Item>
          <Form.Item 
            label="Resource Folder (Google Drive Material Link)" 
            name="driveUrl"
            extra="Paste any Google Drive folder or file link. Ensure permissions are set to 'Anyone with the link can view'."
          >
            <Input placeholder="e.g. https://drive.google.com/drive/folders/..." />
          </Form.Item>
          <Form.Item label="Live Class URL (Zoom Meeting Link)" name="zoomUrl">
            <Input placeholder="e.g. https://zoom.us/j/..." />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Unlock Installment Index (0 = Free)" name="installment" initialValue={0}>
                <Input type="number" min={0} placeholder="e.g. 1, 2" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Free" name="isFree" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Publish" name="isPublished" valuePropName="checked" initialValue={true}>
                <Switch />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item style={{ textAlign: "right", marginTop: "16px" }}>
            <Button style={{ marginRight: 8 }} onClick={() => setIsContentModalOpen(false)}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit">
              {editingContent ? "Update Content" : "Upload Content"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* --- ASSESSMENT CREATION MODAL --- */}
      <Modal
        title={editingAssessment ? "Edit Assessment" : "Create New Assignment / Quiz"}
        visible={isAssessmentModalOpen}
        onCancel={() => {
          setIsAssessmentModalOpen(false);
          setEditingAssessment(null);
          assessmentForm.resetFields();
        }}
        footer={null}
        className="glass-modal"
      >
        <Form form={assessmentForm} layout="vertical" onFinish={handleAssessmentSubmit}>
          <Form.Item
            label="Assessment Name"
            name="name"
            rules={[{ required: true, message: "Please enter assessment name!" }]}
          >
            <Input placeholder="e.g. Midterm Quiz, Assignment 1" />
          </Form.Item>
          <Form.Item label="Instructions / Description" name="description">
            <ReactQuill theme="snow" modules={quillModules} placeholder="Provide instructions for students..." />
          </Form.Item>
          <Form.Item label="Quiz / Google Form Link" name="assessmentUrl">
            <Input placeholder="https://docs.google.com/forms/..." />
          </Form.Item>
          <Row gutter={16} align="middle">
            <Col span={14}>
              <Form.Item label="Due Date / Submission Deadline" name="endDate">
                <Input placeholder="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item label="Publish immediately" name="isPublished" valuePropName="checked" initialValue={true}>
                <Switch />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item style={{ textAlign: "right", marginTop: "16px" }}>
            <Button style={{ marginRight: 8 }} onClick={() => setIsAssessmentModalOpen(false)}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit">
              {editingAssessment ? "Save Changes" : "Create Assessment"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* --- SESSION SCHEDULER MODAL --- */}
      <Modal
        title="Schedule Live Class Session"
        visible={isSessionModalOpen}
        onCancel={() => setIsSessionModalOpen(false)}
        footer={null}
        className="glass-modal"
      >
        <Form form={sessionForm} layout="vertical" onFinish={handleSessionSubmit}>
          <Form.Item
            label="Session Name"
            name="name"
            rules={[{ required: true, message: "Please input session name!" }]}
          >
            <Input placeholder="e.g. Week 4 Live Q&A Discussion" />
          </Form.Item>
          <Form.Item label="Class Date" name="meetingDate" rules={[{ required: true }]}>
            <Input placeholder="YYYY-MM-DD" />
          </Form.Item>
          <Form.Item label="Class Time" name="meetingTime" rules={[{ required: true }]}>
            <Input placeholder="e.g. 06:30 PM" />
          </Form.Item>
          <Form.Item label="Meeting Zoom URL" name="meetingUrl">
            <Input placeholder="https://zoom.us/j/..." />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Meeting ID" name="meetingId">
                <Input placeholder="e.g. 845 2210 5231" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Meeting Password" name="meetingPassword">
                <Input placeholder="e.g. 123456" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Free session" name="isFree" valuePropName="checked" initialValue={false}>
            <Switch />
          </Form.Item>
          <Form.Item style={{ textAlign: "right", marginTop: "16px" }}>
            <Button style={{ marginRight: 8 }} onClick={() => setIsSessionModalOpen(false)}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit">
              Schedule Live Class
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* --- SUBMISSIONS GRADING MODAL --- */}
      <Modal
        title={`Review Submissions: ${selectedGradingAssessment?.name || ""}`}
        visible={isGradingModalOpen}
        onCancel={() => setIsGradingModalOpen(false)}
        footer={null}
        width={750}
        className="glass-modal"
      >
        <Table dataSource={gradingSubmissions} rowKey="id" pagination={{ pageSize: 5 }} className="glass-table" scroll={{ x: 'max-content' }}>
          <Table.Column
            title="Student"
            key="student"
            render={(record) => (
              <Space>
                <Avatar src={record.profileImage} icon={<UserOutlined />} />
                <div>
                  <strong>{record.name}</strong>
                  <p style={{ fontSize: "11px", color: "#999", margin: 0 }}>{record.email}</p>
                </div>
              </Space>
            )}
          />
          <Table.Column
            title="Resource URL"
            dataIndex="submissionUrl"
            key="submissionUrl"
            render={(url) =>
              url ? (
                <a href={url} target="_blank" rel="noreferrer">
                  Open File/Link <EyeOutlined />
                </a>
              ) : (
                "-"
              )
            }
          />
          <Table.Column
            title="Marks & Grade"
            dataIndex="marks"
            key="marks"
            render={(marks) => {
              if (marks === null || marks === undefined || marks === '') {
                return <Tag color="red">Ungraded</Tag>;
              }
              const gradeInfo = getGradeDetails(marks);
              return (
                <Space orientation="vertical" size={2}>
                  <strong>{marks} Marks</strong>
                  <Tag orientation="horizontal" orientationMargin="0" color={gradeInfo.tagColor}>
                    Grade {gradeInfo.grade} • {gradeInfo.classification}
                  </Tag>
                </Space>
              );
            }}
          />
          <Table.Column
            title="Review Feedback"
            key="gradeAction"
            render={(record) => (
              <Form
                layout="inline"
                onFinish={(values) => handleGradeSubmit({ ...values, submissionId: record.id })}
              >
                <Form.Item name="marks" style={{ width: "90px" }} initialValue={record.marks}>
                  <Input placeholder="Score" />
                </Form.Item>
                <Form.Item name="comments" style={{ width: "150px" }} initialValue={record.comments}>
                  <Input placeholder="Comments..." />
                </Form.Item>
                <Form.Item>
                  <Button type="primary" htmlType="submit" size="small">
                    Grade
                  </Button>
                </Form.Item>
              </Form>
            )}
          />
        </Table>
      </Modal>
    </div>
  );
};

export default InstructorPanel;
